import { useEffect, useMemo, useState } from "react";

import {
  compileMerryMarkdown,
  createPendingMerryMarkdownDocument,
  rememberCompiledMerryMarkdown,
} from "./markdownParser";
import {
  compileMerryMarkdownInWorker,
  shouldUseMarkdownWorker,
} from "./markdownWorkerClient";

export interface UseMerryMarkdownDocumentOptions {
  cacheKey?: string | null;
  isStreaming?: boolean;
  source: string;
}

export function useMerryMarkdownDocument({
  cacheKey,
  isStreaming = false,
  source,
}: UseMerryMarkdownDocumentOptions) {
  const workerRequired = shouldUseMarkdownWorker(source);
  const [streamDocument, setStreamDocument] = useState(() =>
    workerRequired
      ? createPendingMerryMarkdownDocument(source)
      : compileMerryMarkdown(source, { cache: false }),
  );

  const completedDocument = useMemo(
    () =>
      isStreaming || workerRequired
        ? null
        : compileMerryMarkdown(source, {
            cache: true,
            cacheKey,
          }),
    [cacheKey, isStreaming, source, workerRequired],
  );

  useEffect(() => {
    if (!workerRequired) return;
    let active = true;
    const frame = window.requestAnimationFrame(() => {
      void compileMerryMarkdownInWorker(source)
        .then((document) => {
          if (!active || document.source !== source) return;
          if (!isStreaming) rememberCompiledMerryMarkdown(document, cacheKey);
          setStreamDocument(document);
        })
        .catch(() => {
          if (!active) return;
          setStreamDocument(
            compileMerryMarkdown(source, { cache: !isStreaming, cacheKey }),
          );
        });
    });
    return () => {
      active = false;
      window.cancelAnimationFrame(frame);
    };
  }, [cacheKey, isStreaming, source, workerRequired]);

  useEffect(() => {
    if (!isStreaming || workerRequired) return;
    const frame = window.requestAnimationFrame(() => {
      setStreamDocument(compileMerryMarkdown(source, { cache: false }));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [isStreaming, source, workerRequired]);

  if (completedDocument) return completedDocument;
  if (streamDocument.source === source) return streamDocument;
  return createPendingMerryMarkdownDocument(source);
}
